import type { ReactNode } from "react";
import type { MediaType } from "@/types";

export interface FilterState {
  region: string;
  year: string;
  genre: string;
  minRating: number;
}

const RATING_OPTIONS = [
  { label: "全部", value: 0 },
  { label: "8.0 分以上", value: 8 },
  { label: "8.5 分以上", value: 8.5 },
  { label: "9.0 分以上", value: 9 },
];

interface FilterPanelProps {
  type: MediaType;
  value: FilterState;
  regions: string[];
  years: number[];
  genres: string[];
  onChange: (patch: Partial<FilterState>) => void;
  onReset: () => void;
}

function Chip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs transition-colors whitespace-nowrap ${
        active
          ? "border-primary-600 bg-primary-600 font-medium text-background-50"
          : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
      }`}
    >
      {children}
    </button>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-foreground-900">{title}</h3>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function FilterPanel({
  type,
  value,
  regions,
  years,
  genres,
  onChange,
  onReset,
}: FilterPanelProps) {
  const typeLabel = type === "movie" ? "影片类型" : type === "tv" ? "剧集类型" : "节目类型";

  return (
    <div className="flex flex-col gap-6">
      <Group title="地区">
        <Chip active={value.region === "全部"} onClick={() => onChange({ region: "全部" })}>
          全部
        </Chip>
        {regions.map((region) => (
          <Chip key={region} active={value.region === region} onClick={() => onChange({ region })}>
            {region}
          </Chip>
        ))}
      </Group>

      <Group title="年份">
        <Chip active={value.year === "全部"} onClick={() => onChange({ year: "全部" })}>
          全部
        </Chip>
        {years.map((year) => (
          <Chip
            key={year}
            active={value.year === String(year)}
            onClick={() => onChange({ year: String(year) })}
          >
            {year}
          </Chip>
        ))}
      </Group>

      <Group title={typeLabel}>
        <Chip active={value.genre === "全部"} onClick={() => onChange({ genre: "全部" })}>
          全部
        </Chip>
        {genres.map((genre) => (
          <Chip key={genre} active={value.genre === genre} onClick={() => onChange({ genre })}>
            {genre}
          </Chip>
        ))}
      </Group>

      <Group title="评分区间">
        {RATING_OPTIONS.map((option) => (
          <Chip
            key={option.label}
            active={value.minRating === option.value}
            onClick={() => onChange({ minRating: option.value })}
          >
            {option.label}
          </Chip>
        ))}
      </Group>

      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
      >
        <i className="ri-refresh-line" aria-hidden="true" />
        重置筛选条件
      </button>
    </div>
  );
}